import { useState, useRef, useEffect } from 'react'
import { IconSearch, IconX, IconMapPin } from '@tabler/icons-react'
import { cn } from 'src/enviroment/lib/utils'
import { mapboxRouter, type GeocodingFeature } from 'src/api/routes/mapbox'
import { Input } from 'src/components/atoms/inputs/Input'

interface MapSearchBarProps {
  onSelect: (center: [number, number], bbox?: [number, number, number, number]) => void
  className?: string
}

const COORD_REGEX = /^\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*$/

function parseCoordinates(query: string): [number, number] | null {
  const match = query.match(COORD_REGEX)
  if (!match) return null
  const lat = parseFloat(match[1])
  const lng = parseFloat(match[2])
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null
  return [lng, lat]
}

export function MapSearchBar({ onSelect, className }: MapSearchBarProps) {
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)

  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300)
    return () => clearTimeout(timer)
  }, [query])

  const coordResult: GeocodingFeature | null = (() => {
    const coords = parseCoordinates(debouncedQuery)
    if (!coords) return null
    return { id: 'coords', place_name: `Go to ${debouncedQuery.trim()}`, center: coords, place_type: ['coordinate'] }
  })()

  const { data: geocodeResults = [], isFetching } = mapboxRouter.geocode.useQuery(debouncedQuery, undefined, {
    enabled: !coordResult,
  })

  const results: GeocodingFeature[] = coordResult ? [coordResult] : geocodeResults

  useEffect(() => {
    setIsOpen(results.length > 0)
  }, [results])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelect = (feature: GeocodingFeature) => {
    onSelect(feature.center, feature.bbox)
    setQuery(feature.place_name)
    setIsOpen(false)
    setActiveIndex(-1)
    inputRef.current?.blur()
  }

  const handleClear = () => {
    setQuery('')
    setDebouncedQuery('')
    setIsOpen(false)
    setActiveIndex(-1)
    inputRef.current?.focus()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || results.length === 0) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((i) => Math.min(i + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault()
      handleSelect(results[activeIndex])
    } else if (e.key === 'Escape') {
      setIsOpen(false)
      inputRef.current?.blur()
    }
  }

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <Input
        ref={inputRef}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value)
          setActiveIndex(-1)
        }}
        onKeyDown={handleKeyDown}
        onFocus={() => results.length > 0 && setIsOpen(true)}
        placeholder="Search places..."
        icon={<IconSearch className="text-light-green-500" />}
        isLoading={isFetching}
        rightElement={
          query && (
            <button onClick={handleClear} className="text-gray-500 transition-colors hover:text-gray-300">
              <IconX size={14} stroke={1.5} />
            </button>
          )
        }
        className="h-auto border-gray-700 bg-gray-800 py-3 text-gray-50"
        autoComplete="off"
      />

      {isOpen && results.length > 0 && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-60 overflow-y-auto rounded-lg border border-gray-700 bg-gray-800 p-1 shadow-xl [&::-webkit-scrollbar-thumb]:bg-gray-600 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-1">
          {results.map((feature, i) => (
            <button
              key={feature.id}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelect(feature)}
              className={cn(
                'flex w-full items-center gap-2 rounded px-3 py-2 text-left transition-colors',
                i === activeIndex ? 'bg-gray-700' : 'hover:bg-gray-700',
              )}
            >
              <IconMapPin size={14} stroke={1.5} className="shrink-0 text-gray-400" />
              <span className="truncate text-sm text-gray-300">{feature.place_name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
